import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/** Marketing and auth pages: show UI immediately; session is resolved in the background. */
function isFastPublicPath(pathname) {
  if (!pathname) return false;
  if (pathname === '/demo') return true;
  if (pathname.startsWith('/shared/')) return true;
  const exact = [
    '/',
    '/about',
    '/pricing',
    '/contact',
    '/privacy',
    '/terms',
    '/login',
    '/register',
    '/docs',
  ];
  if (exact.includes(pathname)) return true;
  if (pathname.startsWith('/reset-password')) return true;
  return false;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() =>
    typeof window !== 'undefined' ? !isFastPublicPath(window.location.pathname) : true
  );
  const authChecked = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      const next = res.data;
      setUser((prev) => {
        if (!next) return null;
        if (prev && prev.user_id === next.user_id && prev.email === next.email) return prev;
        return next;
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (userData) => setUser(userData);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore
    }
    setUser(null);
  }, []);

  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
